import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const RestaurantGalleryScroll = ({ language = 'de' }) => {
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const translations = {
    de: {
      title: 'Galerie',
      restaurantTitle: 'Erleben Sie mehr',
      foodTitle: 'Unsere Küche'
    },
    en: {
      title: 'Gallery',
      restaurantTitle: 'Experience more',
      foodTitle: 'Our Food'
    }
  };

  const t = translations[language];

  // Restaurant images - existing from project
  const restaurantImages = [
    '/galerijaRestoran1.jpeg',
    '/galerijaRestoran2.jpeg',
    '/hotelSlika2.png',
    '/restoranSlikaDodatna.png',
    '/galerijaRestoran3.jpeg',
    '/galerijaRestoran4.jpeg',
    '/galerijaRestoran5.jpeg',
    '/galerijaRestoran6.jpeg',
    '/galerijaRestoran7.jpeg',
    '/restoran1.jpeg',
    '/restoran2.jpeg',
    '/restoran3.jpeg',
    '/restoran4.jpeg'
  ];

  // Food images - from hranaSlike folder (hrana1.jpeg to hrana22.jpeg)
  const foodImages = [
    '/hranaSlike/hrana1.jpeg',
    '/hranaSlike/hrana2.jpeg',
    '/hranaSlike/hrana3.jpeg',
    '/hranaSlike/hrana4.jpeg',
    '/hranaSlike/hrana5.jpeg',
    '/hranaSlike/hrana6.jpeg',
    '/hranaSlike/hrana7.jpeg',
    '/hranaSlike/hrana8.jpeg',
    '/hranaSlike/hrana9.jpeg',
    '/hranaSlike/hrana10.jpeg',
    '/hranaSlike/hrana11.jpeg',
    '/hranaSlike/hrana12.jpeg',
    '/hranaSlike/hrana13.jpeg',
    '/hranaSlike/hrana14.jpeg',
    '/hranaSlike/hrana15.jpeg',
    '/hranaSlike/hrana17.jpeg',
    '/hranaSlike/hrana18.jpeg',
    '/hranaSlike/hrana19.jpeg',
    '/hranaSlike/hrana20.jpeg',
    '/hranaSlike/hrana21.jpeg',
    '/hranaSlike/hrana22.jpeg'
  ];

  // Open fullscreen
  const openFullscreen = (row, index) => {
    setCurrentRow(row);
    setCurrentIndex(index);
    setFullscreenImage(row === 'restaurant' ? restaurantImages[index] : foodImages[index]);
    document.body.style.overflow = 'hidden';
  };

  // Close fullscreen
  const closeFullscreen = () => {
    setFullscreenImage(null);
    setCurrentRow(null);
    setCurrentIndex(0);
    document.body.style.overflow = '';
  };

  // Navigate to next image
  const nextImage = () => {
    const images = currentRow === 'restaurant' ? restaurantImages : foodImages;
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    setFullscreenImage(images[newIndex]);
  };

  // Navigate to previous image
  const prevImage = () => {
    const images = currentRow === 'restaurant' ? restaurantImages : foodImages;
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(newIndex);
    setFullscreenImage(images[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!fullscreenImage) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeFullscreen();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenImage, currentIndex, currentRow]);

  return (
    <>
      <section className="py-20 bg-gradient-to-b from-luxury-pearl via-white to-luxury-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* Title */}
          <h2 className="text-4xl md:text-5xl font-display font-bold text-luxury-charcoal text-center mb-12">
            {t.title}
          </h2>

          {/* Row 1 - Restaurant Images (Auto-scroll Left to Right) */}
          <div className="mb-12">
            <h3 className="text-2xl font-display font-semibold text-luxury-charcoal mb-4 px-2 text-center">
              {t.restaurantTitle}
            </h3>
            <div 
              className="overflow-hidden relative"
              style={{ maxWidth: '100vw' }}
            >
              <div 
                className="flex gap-4 animate-scroll-right"
                style={{
                  width: 'fit-content'
                }}
              >
                {/* Duplicate images for seamless loop */}
                {[...restaurantImages, ...restaurantImages].map((image, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 cursor-pointer group"
                    onClick={() => openFullscreen('restaurant', index % restaurantImages.length)}
                  >
                    <div className="relative w-80 h-64 rounded-xl overflow-hidden shadow-lg transition-transform duration-300 group-hover:scale-105">
                      <img
                        src={image}
                        alt={`Restaurant ${(index % restaurantImages.length) + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      <div className="absolute inset-0 border-2 border-luxury-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 2 - Food Images (Auto-scroll Right to Left) */}
          <div>
            <h3 className="text-2xl font-display font-semibold text-luxury-charcoal mb-4 px-2 text-center">
              {t.foodTitle}
            </h3>
            <div 
              className="overflow-hidden relative"
              style={{ maxWidth: '100vw' }}
            >
              <div 
                className="flex gap-4 animate-scroll-left"
                style={{
                  width: 'fit-content'
                }}
              >
                {/* Duplicate images for seamless loop */}
                {[...foodImages, ...foodImages].map((image, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 cursor-pointer group"
                    onClick={() => openFullscreen('food', index % foodImages.length)}
                  >
                    <div className="relative w-80 h-64 rounded-xl overflow-hidden shadow-lg transition-transform duration-300 group-hover:scale-105">
                      <img
                        src={image}
                        alt={`Food ${(index % foodImages.length) + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      <div className="absolute inset-0 border-2 border-luxury-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Lightbox */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={closeFullscreen}
        >
          {/* Close Button */}
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 md:top-8 md:right-8 z-[10000] bg-luxury-gold hover:bg-luxury-darkGold text-white p-3 rounded-full transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-[10000] bg-luxury-gold/80 hover:bg-luxury-gold text-white p-4 rounded-full transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft size={32} />
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-[10000] bg-luxury-gold/80 hover:bg-luxury-gold text-white p-4 rounded-full transition-colors"
            aria-label="Next"
          >
            <ChevronRight size={32} />
          </button>

          {/* Image */}
          <img
            src={fullscreenImage}
            alt="Gallery"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full">
            <p className="text-white text-sm font-medium">
              {currentIndex + 1} / {currentRow === 'restaurant' ? restaurantImages.length : foodImages.length}
            </p>
          </div>
        </div>
      )}

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        /* Mobile: VERY SLOW - 90 seconds */
        .animate-scroll-left {
          animation: scroll-left 90s linear infinite !important;
        }

        .animate-scroll-right {
          animation: scroll-right 90s linear infinite !important;
        }

        /* Tablet: Medium - 60 seconds */
        @media (min-width: 768px) {
          .animate-scroll-left {
            animation: scroll-left 60s linear infinite !important;
          }
          
          .animate-scroll-right {
            animation: scroll-right 60s linear infinite !important;
          }
        }

        /* Desktop: Fast - 40 seconds */
        @media (min-width: 1024px) {
          .animate-scroll-left {
            animation: scroll-left 40s linear infinite !important;
          }
          
          .animate-scroll-right {
            animation: scroll-right 40s linear infinite !important;
          }
        }

        @keyframes scroll-left {
          0% {
            transform: translateX(0) translateZ(0);
          }
          100% {
            transform: translateX(-50%) translateZ(0);
          }
        }

        @keyframes scroll-right {
          0% {
            transform: translateX(-50%) translateZ(0);
          }
          100% {
            transform: translateX(0) translateZ(0);
          }
        }

        .animate-scroll-right:hover,
        .animate-scroll-left:hover {
          animation-play-state: paused !important;
        }
      `}</style>
    </>
  );
};

export default RestaurantGalleryScroll;
